import * as service from "../service";
import type { ServiceResponse } from "../types";

export type BusinessPlanSnapshot = {
  plan_type: string;
  status: string;
  free_trial_end: string | null;
  current_period_end: string | null;
  is_promo_first_100: boolean;
  pending_plan_type: string | null;
  cancel_at_period_end: boolean;
  card_status: string;
  minimum_term_end: string | null;
  cancel_at: string | null;
  access_status: string;
  has_customer: boolean;
  has_subscription: boolean;
};

export type BusinessAdminState = {
  user_id: string;
  active: boolean;
  plan: BusinessPlanSnapshot | null;
  gateway_configured: boolean;
};

const post = async <T>(
  path: string,
  body: Record<string, any>,
  label: string,
): Promise<ServiceResponse<T>> => {
  try {
    const resp = (await service.call("POST", path, body)) as ServiceResponse<T>;
    if (!resp) return { status: 2 };
    return resp;
  } catch (err) {
    console.error(`${label} ${err}`);
    return { status: -1, error: err };
  }
};

/** Admin-managed state (active flag + subscription) for one business. */
export const GetBusinessAdminAPI = async (
  user_id: string,
): Promise<ServiceResponse<BusinessAdminState>> => {
  try {
    const resp = (await service.call("GET", "/api/business/admin", {
      user_id,
    })) as ServiceResponse<BusinessAdminState>;
    if (!resp) return { status: 2 };
    return resp;
  } catch (err) {
    console.error(`GetBusinessAdminAPI ${err}`);
    return { status: -1, error: err };
  }
};

/** Enable/disable a business account. */
export const SetBusinessActiveAPI = async (
  user_id: string,
  active: boolean,
): Promise<ServiceResponse<{ user_id: string; active: boolean }>> =>
  post("/api/business/admin/set-active", { user_id, active }, "SetBusinessActiveAPI");

/** Reactivate a terminated (canceled/locked) business so it can re-subscribe. */
export const ReactivateBusinessAPI = async (
  user_id: string,
): Promise<ServiceResponse<{ user_id: string; reactivated: boolean }>> =>
  post("/api/business/admin/reactivate", { user_id }, "ReactivateBusinessAPI");

/** Cancel the business' subscription at the end of the current period. */
export const CancelBusinessSubscriptionAPI = async (
  user_id: string,
): Promise<ServiceResponse<Record<string, any>>> =>
  post("/api/business/admin/subscription/cancel", { user_id }, "CancelBusinessSubscriptionAPI");

/** Undo a scheduled cancellation. */
export const ResumeBusinessSubscriptionAPI = async (
  user_id: string,
): Promise<ServiceResponse<Record<string, any>>> =>
  post("/api/business/admin/subscription/resume", { user_id }, "ResumeBusinessSubscriptionAPI");

/** URL of the Stripe Billing Portal session for the business. */
export const OpenBusinessPortalAPI = async (
  user_id: string,
): Promise<ServiceResponse<{ url: string }>> =>
  post("/api/business/admin/subscription/portal", { user_id }, "OpenBusinessPortalAPI");
